import {
  ActionList,
  ActionListItem,
  ActionListSeparator,
  ActionMenu,
  Button,
  Icon,
  PanelContent
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { type EnterpriseMembers } from '~/common/utils/billing-client/client-v2';
import styles from './BulkActionBar.css';

export interface BulkActionBarProps {
  selectedMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  onResendClick: () => void;
  pendingSelectedMembers: EnterpriseMembers.PendingMember[];
  removeSelectedMembers: EnterpriseMembers.PendingOrAcceptedMember[];
  selectedAcceptedMembers: EnterpriseMembers.AcceptedMember[];
  onBulkRemoveClick: () => void;
  isMobile: boolean;
  onBulkEditAccessClick: () => void;
}

const BulkActionBar = ({
  selectedMembers,
  onResendClick,
  pendingSelectedMembers,
  removeSelectedMembers,
  selectedAcceptedMembers,
  onBulkRemoveClick,
  isMobile,
  onBulkEditAccessClick
}: BulkActionBarProps) => {
  const { t } = useTranslation();
  const hasBulkEditAccess = useFeatureFlagState('bulk_edit_access');

  const isRemoveDisabled =
    selectedMembers.length === 1 && removeSelectedMembers.length === 0;
  const displayEditAccess =
    hasBulkEditAccess && selectedAcceptedMembers.length > 0;

  return (
    <PanelContent className={styles.bulkActionPanelContainer}>
      <span data-testid="selected-count">
        Selected: <strong>{selectedMembers.length}</strong>
      </span>
      <div className={styles.bulkActionButtons}>
        {isMobile ? (
          <ActionMenu
            trigger={
              <Button variant="secondary">
                Actions
                <Icon type="chevron-down" />
              </Button>
            }
          >
            <ActionList>
              {displayEditAccess && (
                <ActionListItem
                  leadingIcon="pencil"
                  onAction={onBulkEditAccessClick}
                >
                  <Link to="/user-access/edit-access">{t('Edit Access')}</Link>
                </ActionListItem>
              )}
              {pendingSelectedMembers.length > 0 && (
                <ActionListItem leadingIcon="resend" onAction={onResendClick}>
                  {t('Resend Invites')}
                </ActionListItem>
              )}
              {!isRemoveDisabled && (
                <>
                  {(selectedAcceptedMembers.length > 0 ||
                    pendingSelectedMembers.length > 0) && (
                    <ActionListSeparator />
                  )}
                  <ActionListItem
                    variant="danger"
                    leadingIcon="trash"
                    onAction={onBulkRemoveClick}
                  >
                    {t('Remove')}
                  </ActionListItem>
                </>
              )}
            </ActionList>
          </ActionMenu>
        ) : (
          <>
            {displayEditAccess && (
              <Link
                className="Button--secondary Button--thin"
                to="/user-access/edit-access"
                onClick={e => {
                  if (selectedAcceptedMembers.length > 100) {
                    e.preventDefault();
                    onBulkEditAccessClick();
                  }
                }}
              >
                <Icon type="pencil" />
                {t('Edit Access')}
              </Link>
            )}
            {pendingSelectedMembers.length > 0 && (
              <Button onClick={onResendClick} variant="secondary" thin>
                <Icon type="resend" />
                {t('Resend Invites')}
              </Button>
            )}
            {!isRemoveDisabled && (
              <Button
                onClick={onBulkRemoveClick}
                variant="secondary"
                thin
                disabled={isRemoveDisabled}
              >
                <Icon type="trash" />
                {t('Remove')}
              </Button>
            )}
          </>
        )}
      </div>
    </PanelContent>
  );
};

export default BulkActionBar;
